// opervex-frontend/src/pages/ServiceManagement/Service.js

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Form, Table, InputGroup, Card, Button } from "react-bootstrap";
import { FaSearch, FaPlus, FaSave, FaPencilAlt } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";

import CurrencyInput from "../../components/CurrencyInput";
import { useAuth } from "../../contexts/AuthContext";
import { SearchHelpModal } from "../../utils/SearchHelp";
import useOpervex from "../../hooks/useOpervex";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";

const rolePageRequire = "SERVICES";

var dataDefault = {
  id: "",
  description: "",
  text: "",
  externalCode: "",
  serviceType: null,
  disabled: false,
  prices: [],
};

const actionsDefault = {
  create: true,
  change: true,
  delete: true,
  save: true,
};

const Service = () => {
  const { roles, loadingGlobal } = useAuth();
  const [disabledField, setDisabledField] = useState(true);
  const [actions, setActions] = useState(actionsDefault);
  const [data, setData] = useState(dataDefault);
  const [branches, setBranches] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const { id } = useParams();
  const OpervexAPI = useOpervex();

  useEffect(() => {
    const fetchData = async () => {
      commandAction({ ...actionsDefault, create: false });

      setServiceTypes(await OpervexAPI.ServiceManagement.ServiceType.findAll());

      const fetchedBranches = await OpervexAPI.Accounting.Branches.findAll();
      setBranches(fetchedBranches);

      const updatedPrices = fetchedBranches?.map((branch) => ({
        branch: branch.id,
        description: branch.description,
        points: 0,
        price: 0,
        priceResale: 0,
        priceOs: 0,
      }));

      dataDefault.prices = updatedPrices;

      if (id) {
        await searchAPI(id);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loadingGlobal) {
    return <div>Carregando...</div>;
  }

  const commandAction = (actions) => {
    const hasPermission = roles.find((role) => role.command === rolePageRequire);

    if (!hasPermission) {
      setActions(actionsDefault);
      return;
    }

    if (!actions.create) actions.create = !hasPermission.actions.some((action) => action.key === "Create");
    if (!actions.change) actions.change = !hasPermission.actions.some((action) => action.key === "Change");

    setActions({ ...actions });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    var newData = { ...data };

    switch (name) {
      case "serviceType":
        newData.serviceType = Number(value);
        break;
      default:
        newData = { ...newData, [name]: type === "checkbox" ? checked : value };
        break;
    }

    setData(newData);
  };

  // CRUD Handlers
  const handleButtonCreate = () => {
    setData(dataDefault);
    commandAction({ ...actionsDefault, save: false });
    setDisabledField(false);
  };

  const handleButtonCancel = () => {
    if (data.id === "") {
      setData(dataDefault);
      setDisabledField(true);
      commandAction({ ...actionsDefault, create: false });
    } else {
      searchAPI(data.id);
    }
  };

  const handleButtonSave = async () => {
    const body = { ...data };

    if (data.id) {
      const result = await OpervexAPI.ServiceManagement.Service.update(body.id, body);
      if (result.errors) {
        await ConfirmService.show("Avisos", result.errors, true);
        return;
      }

      await searchAPI(data.id);

      alert("Serviço atualizado com sucesso!");
    } else {
      const result = await OpervexAPI.ServiceManagement.Service.create(body);
      if (result.errors) {
        await ConfirmService.show("Avisos", result.errors, true);
        return;
      }
      await searchAPI(result.id);

      alert("Serviço criado com sucesso!");
    }
  };

  const handleButtonSearch = async () => {
    const item = await SearchHelpModal({
      urlData: "/ServiceManagement/Service/SearchHelp",
      schema: await OpervexAPI.ServiceManagement.Service.schema(),
    });

    if (item && item.id) await searchAPI(item.id);
  };

  const handleButtonChange = async () => {
    setDisabledField(false);
    commandAction({ ...actionsDefault, save: false });
  };

  const searchAPI = async (id) => {
    setDisabledField(true);

    if (!id) return;

    var result = await OpervexAPI.ServiceManagement.Service.findId(id);

    if (result && !result.error) {
      result.prices?.forEach((price) => {
        price.description = branches.find((x) => x.id === price.branch)?.description;
      });

      if (!result.prices || result.prices?.length === 0) result.prices = [];

      branches?.forEach((branch) => {
        if (!result.prices?.find((x) => x.branch === branch.id))
          result.prices.push({
            branch: branch.id,
            description: branch.description,
            points: 0,
            price: 0,
            priceResale: 0,
            priceOs: 0,
          });
      });

      setData(result);
      commandAction({ ...actionsDefault, create: false, change: false });
    } else {
      setData(dataDefault);
      commandAction({ ...actionsDefault, create: false });
      alert(result.error);
    }
  };

  const handleChangePrices = (index, e) => {
    const { name, value } = e.target;

    setData((prev) => ({
      ...prev,
      prices: prev.prices.map((row, i) => (i === index ? { ...row, [name]: value } : row)),
    }));
  };

  return (
    <>
      <Container className="mt-2">
        <Form>
          {/* ----- Header / Additional -----*/}
          <Card className="mb-2">
            <Card.Header>
              <Row className="d-flex justify-content-between">
                <Col md={8}>
                  <h5>Cabeçalho</h5>
                </Col>
                <Col md={4} className="text-end">
                  <Button variant="success" size="sm" className="me-2" onClick={handleButtonCreate} hidden={actions.create}>
                    <FaPlus /> Novo
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonChange} hidden={actions.change}>
                    <FaPencilAlt /> Alterar
                  </Button>
                  <Button variant="danger" size="sm" className="me-2" onClick={handleButtonCancel} hidden={actions.save}>
                    <MdOutlineCancel /> Cancelar
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonSave} hidden={actions.save}>
                    <FaSave /> Salvar
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Código</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.id} name="id" onChange={handleChange} disabled={true} />

                      <InputGroup.Text size="sm" type="button" onClick={handleButtonSearch}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="description"
                      value={data.description}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Check // prettier-ignore
                    type="checkbox"
                    id="disabled"
                    checked={data.disabled}
                    name="disabled"
                    onChange={handleChange}
                    label="Inativo"
                    disabled={disabledField}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
          {/* ----- Additional -----*/}
          <Card className="mb-2">
            <Card.Header>
              <h5>Informações Adicionais</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Label>Texto</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    size="sm"
                    value={data.text}
                    disabled={disabledField}
                    onChange={handleChange}
                    name="text"
                  />
                </Col>
                <Col md={2}></Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Código Externo</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={data?.externalCode}
                      onChange={handleChange}
                      name="externalCode"
                      disabled={disabledField}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Tipo</Form.Label>
                    <Form.Select
                      size="sm"
                      value={data.serviceType ?? ""}
                      onChange={handleChange}
                      name="serviceType"
                      disabled={disabledField}
                    >
                      <option value="">Selecione...</option>
                      {serviceTypes?.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ----- Prices -----*/}
          <Card className="mb-2">
            <Card.Header>
              <h5>Preço do Serviço por Unidade</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr className="text-center align-middle">
                      <th>Unidade</th>
                      <th>Descrição</th>
                      <th>Pontos</th>
                      <th>Preço</th>
                      <th>Preço Revenda</th>
                      <th>Preço por OS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.prices?.length > 0 ? (
                      data.prices.map((price, index) => (
                        <tr key={index}>
                          <td>{price.branch}</td>
                          <td>{branches.find((x) => x.id === price.branch)?.description}</td>
                          <td>
                            {!disabledField ? (
                              <Form.Control
                                type="number"
                                size="sm"
                                value={price.points ? Number(price.points) : 0}
                                onChange={(e) =>
                                  handleChangePrices(index, {
                                    target: {
                                      name: "points",
                                      value: parseInt(e.target.value, 10) || 0,
                                    },
                                  })
                                }
                                disabled={disabledField}
                              />
                            ) : (
                              <>{price.points ? Number(price.points) : 0}</>
                            )}
                          </td>
                          <td>
                            <CurrencyInput
                              name="price"
                              value={price.price}
                              onChange={(e) => handleChangePrices(index, e)}
                              disabled={disabledField}
                            />
                          </td>
                          <td>
                            <CurrencyInput
                              name="priceResale"
                              value={price.priceResale}
                              onChange={(e) => handleChangePrices(index, e)}
                              disabled={disabledField}
                            />
                          </td>
                          <td>
                            <CurrencyInput
                              name="priceOs"
                              value={price.priceOs}
                              onChange={(e) => handleChangePrices(index, e)}
                              disabled={disabledField}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          Nenhum preço encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </Container>
    </>
  );
};

export default Service;
